import type { ChangeEvent } from 'react';
import type { RadarFilters } from '../../types/market';

interface FilterBarProps {
  filters: RadarFilters;
  onChange: (next: RadarFilters) => void;
  marketCount: number;
}

export function FilterBar({ filters, onChange, marketCount }: FilterBarProps): React.JSX.Element {
  const patch = <K extends keyof RadarFilters>(key: K, value: RadarFilters[K]) => onChange({ ...filters, [key]: value });

  return (
    <section className="filter-bar-shell" aria-label="Opportunity filters">
      <div className="filter-bar">
        <div className="filter-field filter-field-grow">
          <label htmlFor="market-search">Search discovered assets</label>
          <div className="input-search-wrapper">
            <input
              id="market-search"
              className="input input-with-icon"
              value={filters.search}
              onChange={(event: ChangeEvent<HTMLInputElement>) => patch('search', event.target.value)}
              placeholder={`Search ${marketCount || 'all'} discovered markets...`}
            />
            {filters.search && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => patch('search', '')}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="filter-field">
          <label htmlFor="horizon">Horizon</label>
          <select id="horizon" className="select" value={filters.horizon} onChange={(event: ChangeEvent<HTMLSelectElement>) => patch('horizon', event.target.value as RadarFilters['horizon'])}>
            <option value="5m">5 minutes</option>
            <option value="15m">15 minutes</option>
            <option value="30m">30 minutes</option>
            <option value="1h">1 hour</option>
            <option value="4h">4 hours</option>
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="direction">Candidate Side</label>
          <select id="direction" className="select" value={filters.direction} onChange={(event: ChangeEvent<HTMLSelectElement>) => patch('direction', event.target.value as RadarFilters['direction'])}>
            <option value="all">Long and Short</option>
            <option value="long">Potential Longs</option>
            <option value="short">Potential Shorts</option>
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="market">Venue Market</label>
          <select id="market" className="select" value={filters.market} onChange={(event: ChangeEvent<HTMLSelectElement>) => patch('market', event.target.value as RadarFilters['market'])}>
            <option value="all">Spot + Futures</option>
            <option value="spot">Spot Only</option>
            <option value="futures">Futures Only</option>
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="volume">Min 24h Vol</label>
          <select id="volume" className="select" value={filters.minQuoteVolume} onChange={(event: ChangeEvent<HTMLSelectElement>) => patch('minQuoteVolume', Number(event.target.value))}>
            <option value={500_000}>$500K</option>
            <option value={1_000_000}>$1M</option>
            <option value={5_000_000}>$5M</option>
            <option value={25_000_000}>$25M</option>
            <option value={100_000_000}>$100M</option>
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="quality">Min Quality</label>
          <select id="quality" className="select" value={filters.minDataQuality} onChange={(event: ChangeEvent<HTMLSelectElement>) => patch('minDataQuality', Number(event.target.value))}>
            <option value={25}>25 · Exploratory</option>
            <option value={45}>45 · Balanced</option>
            <option value={60}>60 · Reliable</option>
            <option value={75}>75 · Strict</option>
          </select>
        </div>
      </div>
    </section>
  );
}
